import { AssetTransferNameMap, AssetTransferType, useAssetTransfer } from '../stores/use-asset-transfer'
import classNames from 'classnames'
import './AssetTransfer.scss'
import { ElForm, ElFormItem } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { SizedBox } from '../components/SizedBox'
import { DialogHeader, HocElDialog, InputView, TransferIcon } from '..'

import { CustomButton } from '../components/Button'
import { DefineComponentProps } from 'utils'
import { PrimaryPlusIcon } from '../icons/PrimaryPlusIcon'


enum MoveState {
  Initial = 0,
  Down = 1,
  Up = 2,
}

export type AssetTransferProps = {
  fromAssetType: AssetTransferType
  toAssetType: AssetTransferType
  onRecharge?: () => void
  onClose?: () => void
}

export const AssetTransfer = defineComponent<AssetTransferProps>({
  props: DefineComponentProps<AssetTransferProps>(['fromAssetType', 'toAssetType', 'onRecharge', 'onClose']),
  setup(props) {
    const isOpen = ref(false)

    const assetTransfer = useAssetTransfer()
    const { t } = useI18n()
    const isMove = ref(MoveState.Initial)

    onBeforeMount(() => {
      assetTransfer.setFromAssetType(props.fromAssetType)
      assetTransfer.setToAssetType(props.toAssetType)
      assetTransfer.open()
    })

    const transformFromAndToAssetType = () => {
      if (isMove.value === MoveState.Initial) {
        isMove.value = MoveState.Down
      } else if (isMove.value === MoveState.Down) {
        isMove.value = MoveState.Up
      } else if (isMove.value === MoveState.Up) {
        isMove.value = MoveState.Initial
      }
      setTimeout(() => {
        assetTransfer.transformFromAndToAssetType()
        isMove.value = MoveState.Initial
      }, 300)
    }

    const tips = computed(() => {
      if (assetTransfer.fromAssetType.value === AssetTransferType.Contract) {
        return ['最多可划转 = 合约账户余额 - 全仓占用资金 - 合约赠金 - HXUSDT质押金额', '全仓占用资金：全仓持仓中存在未实现盈亏及维持保证金情况，需要动态锁定部分余额资金。']
      }
      return ['最多可划转 = 合约账户余额 - 全仓占用资金 - 合约赠金 - HXUSDT质押金额', '全仓占用资金：全仓持仓中存在未实现盈亏及维持保证金情况，需要动态锁定部分余额资金。']
    })

    const onClose = () => {
      assetTransfer.close()
    }

    watch(
      () => assetTransfer.assetTransferVisible.value,
      (val) => {
        if (!val) {
          setTimeout(() => {
            props.onClose?.()
          }, 300)
        }
      },
    )

    const onRecharge = () => {
      assetTransfer.close()
      props.onRecharge?.()
    }

    const onShowDialog = () => {
      isOpen.value = !isOpen.value
    }

    const onAllAmount = () => {
      assetTransfer.formData.value.amount = assetTransfer.maxTransferAmount.value
    }

    const isContract = computed(() => assetTransfer.fromAssetType.value === AssetTransferType.Contract)

    return () => (
      <HocElDialog v-model={assetTransfer.assetTransferVisible.value} showClose={false} closeOnClickModal={false} width={600}>
        {{
          header: () => <DialogHeader onClose={onClose}>{t('资产划转')}</DialogHeader>,
          default: () => (
            <div class="asset-transfer-layout">
              <HocElDialog v-model={isOpen.value} showClose={false} closeOnClickModal={false} footer={null} width={400}>
                {{
                  header: () => <DialogHeader onClose={onShowDialog}>{t('最多可划转说明')}</DialogHeader>,
                  default: () => (
                    <>
                      <ul class="text-[14px] text-wrap">
                        <li>{t('最多可划转 = 合约账户余额 - 全仓占用资金 - 合约赠金')}</li>
                        <li>{t('全仓占用资金：全仓持仓中存在未实现盈亏及维持保证金情况，需要动态锁定部分余额资金。')}</li>
                      </ul>
                      <CustomButton type="primary" class="ok-btn" onClick={onShowDialog}>
                        {t('我知道了')}
                      </CustomButton>
                    </>
                  ),
                }}
              </HocElDialog>
              <div class="asset-tabs">
                <div class={classNames('wallet-tab', { initial: isMove.value === MoveState.Initial })}>
                  <div class="from wallet-tab-item">
                    <div class="tab-source">{t('从')}</div>
                    <div class={classNames('tab-source-name from-source', { active: isMove.value === MoveState.Down })} data-type={assetTransfer.fromAssetType.value}>
                      {t(AssetTransferNameMap[assetTransfer.fromAssetType.value])}
                    </div>
                  </div>
                  <div class="to wallet-tab-item">
                    <div class="tab-source">{t('至')}</div>
                    <div class={classNames('tab-source-name to-source', { active: isMove.value === MoveState.Down })} data-type={assetTransfer.toAssetType.value}>
                      {t(AssetTransferNameMap[assetTransfer.toAssetType.value])}
                    </div>
                  </div>
                </div>
                <div class="transform-btn" onClick={transformFromAndToAssetType}>
                  <TransferIcon />
                </div>
              </div>
              <SizedBox height={24} />
              <ElForm labelPosition="top">
                <ElFormItem label={t('币种')}>
                  <div class="coin-box">
                    <div class="coin-icon">{assetTransfer.itemIcon.value ? <img width={24} height={24} src={assetTransfer.itemIcon.value} alt="" /> : null}</div>
                    <span class="coin-name">{assetTransfer.itemName.value}</span>
                  </div>
                </ElFormItem>
                <ElFormItem label={t('划转数量')}>
                  <InputView v-model={assetTransfer.formData.value.amount} isFloatNumber maxDecimalLength={2} maxIntegerLength={9} placeholder={t('请输入划转数量')} radius={8}>
                    {{
                      suffix: () => (
                        <span class={'all-text'} onClick={onAllAmount}>
                          {t('全部')}
                        </span>
                      ),
                    }}
                  </InputView>
                  <div class="max-transfer-amount">
                    <span>{t('最多可划转')}:</span>&nbsp;
                    <span class={classNames({ dashedUnderline: isContract.value })} onClick={onShowDialog}>
                      {assetTransfer.maxTransferAmount.value.formatNumberWithCommas().decimalPadZero(2)} USDT
                    </span>
                    <span class="recharge-text" onClick={onRecharge}>
                      <PrimaryPlusIcon />
                    </span>
                  </div>
                </ElFormItem>

                <ElFormItem>
                  <div class="transfer-tips">
                    <p>{t('最多可划转说明')}</p>
                    {tips.value.map((item) => (
                      <p>{t(item)}</p>
                    ))}
                  </div>
                </ElFormItem>
                <SizedBox height={48} />
                <ElFormItem style={{ marginBottom: 0 }}>
                  <CustomButton class={'transfer-confirm-btn'} type="primary" loading={assetTransfer.loading.value} onClick={assetTransfer.onTransfer}>
                    {t('划转')}
                  </CustomButton>
                </ElFormItem>
              </ElForm>
            </div>
          ),
        }}
      </HocElDialog>
    )
  },
})
